import { useEffect, useMemo, useRef, useState } from "react";
import api from "../../api/api";
import { V2_BASE_URL } from "../../api/bases";
import { getPracticeId } from "../../config/practice";

/*
 * Report a Problem (patient app, 10-23). A patient's report arrives in the chat as a NORMAL
 * message ("Problem report #PR-10432: Won't load · Appointments — note"); the v2 staff routes
 * say which chat message belongs to which report:
 *   GET  /v1/{pid}/problem-reports?status=all&patientid=  -> items[{report_id, reference, status,
 *        category_label, screen_name, chat_details_id, screenshot{...}|null, ...}]
 *   GET  /v1/{pid}/problem-reports/{report_id}/screenshot?size=thumb|full   (image; audited)
 *   POST /v1/{pid}/problem-reports/{report_id}/resolve {note?}
 * A chip is shown only for the chat message whose id is the report's chat_details_id.
 * Resolve answers 409 ALREADY_RESOLVED when someone else resolved it first (shown as Resolved).
 * All pinned to v2 (the routes exist only there). Any failure (no reports table yet, an account
 * that may not use it) = no reports: the chat renders exactly as before.
 */
const V2 = { baseURL: V2_BASE_URL, _gw: "v2-pinned" };
const REF_RE = /^Problem report #(PR-\d+)/;
const enc = (s) => encodeURIComponent(String(s));

export const referenceOf = (text) => {
  const m = REF_RE.exec(String(text || ""));
  return m ? m[1] : null;
};

export async function listPatientReports(patientid) {
  const body = await api.get(`/v1/${getPracticeId()}/problem-reports`, {
    ...V2,
    params: { status: "all", patientid, page_size: 100 },
  });
  return Array.isArray(body?.items) ? body.items : [];
}

export async function fetchScreenshotUrl(reportId, size = "thumb") {
  const blob = await api.get(`/v1/${getPracticeId()}/problem-reports/${enc(reportId)}/screenshot`, {
    ...V2,
    params: { size },
    responseType: "blob",
  });
  return URL.createObjectURL(blob);
}

export async function resolveReport(reportId, note) {
  return api.post(`/v1/${getPracticeId()}/problem-reports/${enc(reportId)}/resolve`, note ? { note } : {}, V2);
}

/**
 * Reports of the open patient, looked up by chat message: { reportFor(message), markResolved(id) }.
 * Re-fetches when the chat shows a "Problem report #PR-…" message it does not know yet.
 */
export function useProblemReports(patientid, messages) {
  const [items, setItems] = useState([]);
  const fetchedFor = useRef("");

  const unknownRefs = useMemo(() => {
    const known = new Set(items.map((r) => r.reference));
    const refs = [];
    (messages || []).forEach((m) => {
      const ref = m && m.is_reply === 1 ? referenceOf(m.message) : null;
      if (ref && !known.has(ref)) refs.push(ref);
    });
    return refs.sort().join(",");
  }, [items, messages]);

  useEffect(() => {
    setItems([]);
    fetchedFor.current = "";
  }, [patientid]);

  useEffect(() => {
    if (!patientid || !unknownRefs) return undefined;
    const key = `${patientid}:${unknownRefs}`;
    if (fetchedFor.current === key) return undefined;
    fetchedFor.current = key;
    let alive = true;
    listPatientReports(patientid)
      .then((list) => { if (alive) setItems(list); })
      .catch(() => {});
    return () => { alive = false; };
  }, [patientid, unknownRefs]);

  // Matched by chat_details_id ONLY: the message text is patient-controlled, so a typed
  // "Problem report #PR-…" never gets a chip (the text only decides when to re-fetch the list).
  const byChat = useMemo(() => {
    const m = new Map();
    items.forEach((r) => {
      if (r.chat_details_id !== undefined && r.chat_details_id !== null) m.set(String(r.chat_details_id), r);
    });
    return m;
  }, [items]);

  const reportFor = (message) => {
    if (!message || message.is_reply !== 1 || message.id === undefined || message.id === null) return null;
    return byChat.get(String(message.id)) || null;
  };

  const markResolved = (reportId, patch) =>
    setItems((prev) => prev.map((r) => (r.report_id === reportId ? { ...r, ...patch, status: "resolved" } : r)));

  return { reportFor, markResolved };
}
